import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    News, Billing, SaleAgent, Order, DetailedOrder, PromotionalProduct, PromotionalOrder, PromotionalDelivery, GroupsAndProducts, UserDeliveryAddress,
    RegisteredUserData, LocalCart, ProductInLocalCart, ProductPrice, ProductsGroup, GutteringSystemData,
    NotificationComponentData, WebserviceTestResult, ServiceWorkType, OrderStatus, OrderStatusNames, OrderStatusName,
    CacheManagement,
    Statistics,
    TermsResponse,
    AdditionalTab,
    ShutdownHours,
    SchemaValidationResults,
    SchemaValidationResultsResponse,
    SchemaValidationLatestResult,
    SchemaValidationLatestResultsResponse,
    ProductInGroup,
    Product,
    CartTableContent,
    CartGroup,
    IncorrectImportedProduct
} from 'lib/types';
import _ from 'lodash';
import { TreeNode } from 'lib/tree';
import { NewsResponse } from 'lib/communication/communicationTypes';

const findSimilarProductInLocalCartIndex = (productsInLocalCart: ProductInLocalCart[], searchedProduct: ProductInLocalCart): number => _.findIndex(productsInLocalCart, (product) => (
    product.cartGroup === searchedProduct.cartGroup &&
    product.product.symKar === searchedProduct.product.symKar &&
    product.mainProductSymkar === searchedProduct.mainProductSymkar &&
    product.dimension === searchedProduct.dimension &&
    product.unit.unit === searchedProduct.unit.unit &&
    _.isEqual(product.systemData, searchedProduct.systemData) &&
    _.isEqual(product.additionsSelected, searchedProduct.additionsSelected)
));

export const Messages = {
    noMessage: 'noMessage',
    cartMessage: 'cartMessage',
    additionalAssortmentMessage: 'additionalAssortmentMessage'
};
export type Messages = typeof Messages[keyof typeof Messages];

export interface UserRegistrationInfo {
    user: {
        firstName: string;
        lastName: string;
        nip?: string;
        treasuryOffice?: string;
        pesel?: string;
        phone?: string;
        email: string;
        street?: string;
        buildingNo?: string;
        apartmentNo?: string;
        postalCode?: string;
        town?: string;
    };
    company: {
        name?: string;
        street?: string;
        streetNumber?: string;
        flatNumber?: string;
        postalCode?: string;
        city?: string;
        regon?: string;
    };
}

const dataSlice = createSlice({
    name: 'data',
    initialState: {
        notificationComponent: {
            props: {
                isVisible: false as boolean
            },
            notificationMessage: '' as string,
            notificationVariant: '' as string
        },
        userInfo: {
            props: {
                isFetching: false as boolean
            },
            userInfo: null as UserRegistrationInfo | null,
            userDeliveryAddresses: null as UserDeliveryAddress[] | null,
            currentOrIncomigServiceWorks: {} as ServiceWorkType,
            servicesAvailableDuringServiceWork: ['isod'] as string[],
            justLoggedIn: false as boolean,
            shutdownHours: {} as ShutdownHours
        },
        news: {
            props: {
                isFetching: false as boolean
            },
            news: [] as News[],
            totalCount: 0,
            isNewsGraphicsFetching: false,
            isNewsDumpFetching: false
        },
        orders: {
            props: {
                isFetching: false,
                pdfIsFetching: false,
                isFetchingGroups: false,
                isFetchingOrders: false,
                isErrorGettingDetailedOrder: false,
                isAddingOrder: false,
                isGutteringsDownloaded: false,
                isCartFetching: true,
                isSearchingProducts: false,
                isFetchingProductInfo: false,
                productsOfGroupsFetching: [] as string[],
                isOrderStatusFetching: false,
                isProductDetailsFetching: false,
                isAdditionalAssortmentInfoFetching: false
            },
            orders: Object.fromEntries(_.map(OrderStatusNames, (name) => [name, null])) as { [NAME in OrderStatusName]: Order[] | null },
            localCart: { productsInLocalCart: [] } as LocalCart,
            cartProdNotAvailable: false,
            previousSummaryPrice: null as number | null,
            cartTableContent: {} as CartTableContent,
            append: false as boolean,
            appendNagIdSource: null as string | null,
            edit: false as boolean,
            additionalAssortmentPrice: [] as ProductPrice[],
            groupsAndProducts: {} as GroupsAndProducts,
            detailedOrder: {} as DetailedOrder,
            advancedTree: null as TreeNode[] | null,
            orderStatusInfo: {
                orderStatus: {} as OrderStatus,
                show: false
            },
            editImageUploadFetching: false,
            editImagesUploadFetching: false,
            isAssortmentGraphicsFetching: false as boolean,
            showCartMessage: Messages.noMessage,
            importsWithoutProperUnit: [] as IncorrectImportedProduct[]
        },
        settlements: {
            props: {
                settlementsIsFetching: false as boolean,
                fileIsFetching: false as boolean
            },
            settlements: [] as Billing[]
        },
        promotionalProgram: {
            props: {
                isFetching: false,
                isFetchingProducts: false,
                isFetchingPoints: false,
                isAddingOrder: false,
                traderBlockIsFetching: false
            },
            salesAgents: [] as SaleAgent[],
            groups: [] as string[],
            products: [] as PromotionalProduct[],
            delivery: {} as PromotionalDelivery,
            orders: [] as PromotionalOrder[],
            points: 0,
            pointsSpent: 0,
            termsAndConditions: {} as TermsResponse,
            registeredUserData: {} as RegisteredUserData,
            cookiePolicy: {} as TermsResponse,
            promotionProgramPolicy: {} as TermsResponse
        },
        webserviceTestResults: {
            props: {
                isFetching: false as boolean
            },
            results: [] as WebserviceTestResult[]
        },
        schemaValidationResults: {
            props: {
                isFetching: false as boolean,
                isFetchingLatestResults: false as boolean
            },
            results: {} as SchemaValidationResults,
            latestResults: {} as SchemaValidationLatestResult[],
            errorsOccured: false as boolean,
            latestResultsErrorsOccured: false as boolean
        },
        adminInfo: {
            props: {
                isFetching: false
            },
            serviceWorks: [] as ServiceWorkType[],
            cacheManagement: [] as CacheManagement[],
            isCacheManagementFetching: false as boolean,
            statistics: {} as Statistics,
            shutdownHours: {} as ShutdownHours
        },
        gutterCreator: {
            props: {
                filters: {} as GutteringSystemData,
                toBeUpdated: false
            }
        },
        additionalTab: {
            props: {
                isFetching: false as boolean,
                isBeingUpdated: false as boolean
            },
            details: {} as AdditionalTab
        }
    },
    reducers: {
        setGutterCreatorFilters(state, action: PayloadAction<GutteringSystemData>) {
            state.gutterCreator.props.filters = action.payload;
        },
        setGutterToBeUpdated(state, action: PayloadAction<boolean>) {
            state.gutterCreator.props.toBeUpdated = action.payload;
        },
        setGutterCreatorFilter(state, action: PayloadAction<{ filterName: string, value: string }>) {
            state.gutterCreator.props.filters[action.payload.filterName as keyof GutteringSystemData] = action.payload.value;
        },
        setIsCacheManagementFetching(state, action: PayloadAction<boolean>) {
            state.adminInfo.props.isFetching = action.payload;
        },
        setCacheManagement(state, action: PayloadAction<CacheManagement[]>) {
            state.adminInfo.cacheManagement = action.payload;
        },
        setAdminInfoFetching(state, action: PayloadAction<boolean>) {
            state.adminInfo.props.isFetching = action.payload;
        },
        setServiceWorks(state, action: PayloadAction<ServiceWorkType[]>) {
            state.adminInfo.serviceWorks = action.payload;
        },
        setStatistics(state, action: PayloadAction<Statistics>) {
            state.adminInfo.statistics = action.payload;
        },
        setCurrentOrIncomingServiceWorks(state, action: PayloadAction<ServiceWorkType>) {
            state.userInfo.currentOrIncomigServiceWorks = action.payload;
        },
        clearUserDeliveryAddresses(state) {
            state.userInfo.userDeliveryAddresses = null;
        },
        setUserDeliveryAddresses(state, action: PayloadAction<UserDeliveryAddress[]>) {
            state.userInfo.userDeliveryAddresses = action.payload;
        },
        setUserInfo(state, action: PayloadAction<UserRegistrationInfo | null>) {
            state.userInfo.userInfo = action.payload;
        },
        setUserInfoFetching(state, action: PayloadAction<boolean>) {
            state.userInfo.props.isFetching = action.payload;
        },
        setUserJustLoggedIn(state, action: PayloadAction<boolean>) {
            state.userInfo.justLoggedIn = action.payload;
        },
        setNewsData(state, action: PayloadAction<NewsResponse>) {
            state.news.news = action.payload.news;
            state.news.totalCount = action.payload.totalCount;
        },
        updateNews(state, action: PayloadAction<News>) {
            const index = state.news.news.findIndex((e) => e.id === action.payload.id);
            if (index >= 0) {
                state.news.news[index] = action.payload;
            }
        },
        setIsNewsArchived(state, action: PayloadAction<{ id: number, value: boolean }>) {
            const index = state.news.news.findIndex((e) => e.id === action.payload.id);
            if (index >= 0) {
                state.news.news[index].isArchived = action.payload.value;
            }
        },
        setIsNewsPinned(state, action: PayloadAction<{ id: number, value: boolean }>) {
            const index = state.news.news.findIndex((e) => e.id === action.payload.id);
            if (index >= 0) {
                state.news.news[index].pinned = action.payload.value;
            }
        },
        setNewsFetching(state, action: PayloadAction<boolean>) {
            state.news.props.isFetching = action.payload;
        },
        setSettlementsList(state, action: PayloadAction<Billing[]>) {
            state.settlements.settlements = action.payload;
        },
        setSettlements(state, action: PayloadAction<any>) {
            state.settlements = action.payload;
        },
        setPromotionalProgram(state, action: PayloadAction<Object>) {
            state.promotionalProgram = {
                ...state.promotionalProgram,
                ...action.payload
            };
        },
        setPromotionalProgramPoints(state, action: PayloadAction<number>) {
            state.promotionalProgram.points = action.payload;
        },
        setPromotionalProgramPointsSpent(state, action: PayloadAction<number>) {
            state.promotionalProgram.pointsSpent = action.payload;
        },
        setSaleAgents(state, action: PayloadAction<SaleAgent[]>) {
            state.promotionalProgram.salesAgents = action.payload;
        },
        activateSalesAgent(state, action: PayloadAction<SaleAgent>) {
            const index = state.promotionalProgram.salesAgents.findIndex((e) => e.login === action.payload.login);
            if (index >= 0) {
                state.promotionalProgram.salesAgents[index].blocked = false;
            }
        },
        blockSalesAgent(state, action: PayloadAction<SaleAgent>) {
            const index = state.promotionalProgram.salesAgents.findIndex((e) => e.login === action.payload.login);
            if (index >= 0) {
                state.promotionalProgram.salesAgents[index].blocked = true;
            }
        },
        setGroupsAndProducts(state, action: PayloadAction<GroupsAndProducts>) {
            state.orders.groupsAndProducts = action.payload;
        },
        clearGroupsAndProducts(state) {
            state.orders.groupsAndProducts = {} as GroupsAndProducts;
        },
        setGroups(state, action: PayloadAction<ProductsGroup[]>) {
            state.orders.groupsAndProducts.groups = action.payload;
        },
        setOrders(state, action: PayloadAction<any>) {
            state.orders = action.payload;
        },
        addProductsInGroup(state, action: PayloadAction<ProductInGroup[]>) {
            const oldProducts = _.filter(state.orders.groupsAndProducts.productsInGroup, (product) => !_.includes(
                _.map(action.payload, (fetchedProduct) => fetchedProduct.symKar),
                product.symKar
            ));
            state.orders.groupsAndProducts.productsInGroup = [...oldProducts, ...action.payload].sort(({ description: a }, { description: b }) => a.localeCompare(b));
        },
        addProducts(state, action: PayloadAction<Product[]>) {
            const oldProducts = _.filter(state.orders.groupsAndProducts.products, (product) => !_.includes(
                _.map(action.payload, (fetchedProduct) => fetchedProduct.symKar),
                product.symKar
            ));
            state.orders.groupsAndProducts.products = [...oldProducts, ...action.payload].sort(({ description: a }, { description: b }) => a.localeCompare(b));
        },
        setIsProductDetailsFetching(state, action: PayloadAction<boolean>) {
            state.orders.props.isProductDetailsFetching = action.payload;
        },
        setAdditionalAssortmentDetailsFetching(state, action: PayloadAction<boolean>) {
            state.orders.props.isAdditionalAssortmentInfoFetching = action.payload;
        },
        setOrdersList(state, action: PayloadAction<{ status: OrderStatusName, data: Order[] }>) {
            state.orders.orders[action.payload.status] = action.payload.data;
        },
        resetOrdersList(state) {
            state.orders.orders = Object.fromEntries(_.map(OrderStatusNames, (name) => [name, null])) as { [NAME in OrderStatusName]: Order[] | null };
        },
        setDetailedOrder(state, action: PayloadAction<DetailedOrder>) {
            state.orders.detailedOrder = action.payload;
        },
        setCartTableContent(state, action: PayloadAction<CartTableContent>) {
            state.orders.cartTableContent = action.payload;
            state.orders.previousSummaryPrice = null;
        },
        setEdit(state, action: PayloadAction<boolean>) {
            state.orders.edit = action.payload;
        },
        setAppend(state, action: PayloadAction<boolean>) {
            state.orders.append = action.payload;
        },
        setAppendNagIdSource(state, action: PayloadAction<string | null>) {
            state.orders.appendNagIdSource = action.payload;
        },
        addToLocalCart(state, action: PayloadAction<{ product: ProductInLocalCart }>) {
            const products = state.orders.localCart.productsInLocalCart;

            const similarProductIndex = findSimilarProductInLocalCartIndex(products, action.payload.product);

            if (similarProductIndex > -1) {
                const [product] = products.splice(similarProductIndex, 1);
                if (product.cartGroup === CartGroup.gutterings) {
                    product.quantity = action.payload.product.quantity;
                } else {
                    product.quantity += action.payload.product.quantity;
                }
                if (action.payload.product.quantity !== 0) products.push(product);
            } else if (action.payload.product.quantity !== 0) {
                products.push(action.payload.product);
            }
        },
        clearAdditionalAssortmentPrice(state, action: PayloadAction<number>) {
            state.orders.additionalAssortmentPrice = Array<ProductPrice>(action.payload);
        },
        setAdditionalAssortmentPrice(state, action: PayloadAction<{ productPrice: ProductPrice, index: number }>) {
            state.orders.additionalAssortmentPrice[action.payload.index] = action.payload.productPrice;
        },
        deleteFromLocalCart(state, action: PayloadAction<{ index: number }>) {
            state.orders.localCart.productsInLocalCart.splice(action.payload.index, 1);
        },
        editProductsInLocalCart(state, action: PayloadAction<{ index: number, product: ProductInLocalCart }>) {
            const { productsInLocalCart } = state.orders.localCart;
            const similarProductIndex = findSimilarProductInLocalCartIndex(productsInLocalCart, action.payload.product);

            if (similarProductIndex >= 0 && similarProductIndex !== action.payload.index) {
                productsInLocalCart[similarProductIndex].quantity += action.payload.product.quantity;
                productsInLocalCart.splice(action.payload.index, 1);
            } else {
                productsInLocalCart[action.payload.index] = action.payload.product;
            }
        },
        clearIsodCart(state) {
            const newProductsInLocalCart: ProductInLocalCart[] = [];
            state.orders.localCart.productsInLocalCart.forEach((prd) => {
                if (prd.cartGroup !== CartGroup.isod) newProductsInLocalCart.push(prd);
            });
            state.orders.localCart.productsInLocalCart = newProductsInLocalCart;
        },
        setLocalCart(state, action: PayloadAction<LocalCart>) {
            state.orders.localCart = action.payload;
        },
        setCartProdNotAvailable(state, action: PayloadAction<boolean>) {
            state.orders.cartProdNotAvailable = action.payload;
        },
        setPreviousSummaryPrice(state, action: PayloadAction<number | null>) {
            state.orders.previousSummaryPrice = action.payload;
        },
        clearLocalCart(state) {
            state.orders.localCart = { productsInLocalCart: [] } as LocalCart;
        },
        clearCart(state) {
            state.orders.cartTableContent = {} as CartTableContent;
        },
        setAdvancedTree(state, action: PayloadAction<TreeNode[]>) {
            state.orders.advancedTree = action.payload;
        },
        setIsErrorGettingDetailedOrder(state, action: PayloadAction<boolean>) {
            state.orders.props.isErrorGettingDetailedOrder = action.payload;
        },
        clearLocalGutteringsCart(state, action: PayloadAction<{ gutteringSystemData: GutteringSystemData }>) {
            const { productsInLocalCart } = state.orders.localCart;
            _.remove(productsInLocalCart, (element) => _.isEqual(element.systemData, action.payload.gutteringSystemData));
        },
        setWebserviceTestsFetching(state, action: PayloadAction<boolean>) {
            state.webserviceTestResults.props.isFetching = action.payload;
        },
        setWebserviceTestsResults(state, action: PayloadAction<WebserviceTestResult[]>) {
            state.webserviceTestResults.results = action.payload;
        },
        setWebserviceTests(state, action: PayloadAction<any>) {
            state.webserviceTestResults = action.payload;
        },
        setSchemaValidationResults(state, action: PayloadAction<SchemaValidationResultsResponse>) {
            state.schemaValidationResults.results = action.payload.results;
            state.schemaValidationResults.errorsOccured = action.payload.errorsOccured;
        },
        setLatestSchemaValidationResults(state, action: PayloadAction<SchemaValidationLatestResultsResponse>) {
            state.schemaValidationResults.latestResults = action.payload.results;
            state.schemaValidationResults.latestResultsErrorsOccured = action.payload.errorsOccured;
        },
        setWebServiceSchemaValidationResultsFetching(state, action: PayloadAction<boolean>) {
            state.schemaValidationResults.props.isFetching = action.payload;
        },
        setWebServiceLatestSchemaValidationResultsFetching(state, action: PayloadAction<boolean>) {
            state.schemaValidationResults.props.isFetchingLatestResults = action.payload;
        },
        setCartFetching(state, action: PayloadAction<boolean>) {
            state.orders.props.isCartFetching = action.payload;
        },
        setNotificationComponent(state, action: PayloadAction<NotificationComponentData>) {
            state.notificationComponent.props.isVisible = action.payload.isVisible;
            state.notificationComponent.notificationMessage = action.payload.message;
            state.notificationComponent.notificationVariant = action.payload.variant;
        },
        hideNotificationComponent(state) {
            state.notificationComponent.props.isVisible = false;
        },
        setProductsOfGroupsFetching(state, action: PayloadAction<string[]>) {
            state.orders.props.productsOfGroupsFetching = action.payload;
        },
        setIsOrderStatusFetching(state, action: PayloadAction<boolean>) {
            state.orders.props.isOrderStatusFetching = action.payload;
        },
        setOrderStatus(state, action: PayloadAction<OrderStatus>) {
            state.orders.orderStatusInfo.orderStatus = action.payload;
        },
        setShowOrderStatus(state, action: PayloadAction<boolean>) {
            state.orders.orderStatusInfo.show = action.payload;
        },
        setIsEditImageUploadFetching(state, action: PayloadAction<boolean>) {
            state.orders.editImageUploadFetching = action.payload;
        },
        setIsEditImagesUploadFetching(state, action: PayloadAction<boolean>) {
            state.orders.editImagesUploadFetching = action.payload;
        },
        setIsAssortmentGraphicsFetching(state, action: PayloadAction<boolean>) {
            state.orders.isAssortmentGraphicsFetching = action.payload;
        },
        setIsNewsGraphicsFetching(state, action: PayloadAction<boolean>) {
            state.news.isNewsGraphicsFetching = action.payload;
        },
        setIsNewsDumpFetchng(state, action: PayloadAction<boolean>) {
            state.news.isNewsDumpFetching = action.payload;
        },
        setIsAdditionalTabFetching(state, action: PayloadAction<boolean>) {
            state.additionalTab.props.isFetching = action.payload;
        },
        setAdditionalTabDetails(state, action: PayloadAction<AdditionalTab>) {
            state.additionalTab.details = {
                ...state.additionalTab.details,
                ...action.payload
            };
        },
        setIsAdditionalTabBeingUpdated(state, action: PayloadAction<boolean>) {
            state.additionalTab.props.isBeingUpdated = action.payload;
        },
        setShutdownHoursUserInfo(state, action: PayloadAction<ShutdownHours>) {
            state.userInfo.shutdownHours = action.payload;
        },
        setShutdownHoursAdminInfo(state, action: PayloadAction<ShutdownHours>) {
            state.adminInfo.shutdownHours = action.payload;
        },
        setShowCartMessage(state, action: PayloadAction<Messages>) {
            state.orders.showCartMessage = action.payload;
        },
        setImportsWithoutProperUnit(state, action: PayloadAction<IncorrectImportedProduct[]>) {
            state.orders.importsWithoutProperUnit = action.payload;
        }
    }
});

export default dataSlice.reducer;

export const dataActions = dataSlice.actions;
